import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
  Component,
  computed,
  effect,
  inject,
  Signal,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { Measure } from '@api-types/enums/measure.enum';
import { Resolution } from '@api-types/enums/resolution.enum';
import { Widget, WidgetType } from '@api-types/enums/widget-type';
import {
  absoluteErrorMeasureOptions,
  relativeErrorMeasureOptions,
  TOTALS_NAME,
} from '@api-types/helper/helper';
import { FilterValue } from '@api-types/types/filter-value.type';
import {
  FormFieldComponent,
  FormInputNumberComponent,
  FormInputSelectComponent,
  FormInputTextComponent,
  PmButtonComponent,
  PmDialogComponent,
  PmIconComponent,
  PmTooltipDirective,
} from '@libs/ui';

import { NgxVirtualSelectFieldOptionModel } from '@components/base-elements/virtual-select-field';
import { LucideGripVertical, LucidePlus, LucideTrash } from '@lucide/angular';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { TargetUnitPickerComponent } from 'demand-forecasting/components/forecast-detail/target-unit-picker/target-unit-picker.component';
import { ConfigurationHttpClient } from 'demand-forecasting/services/http/configuration-http-client';
import { ConfigurationsSelectors } from 'demand-forecasting/store/configurations/configurations.selectors';
import { selectAvailableFiltersAsRecord } from 'demand-forecasting/store/filter/filter.selectors';
import { selectForecastSeriesPossibleYears } from 'demand-forecasting/store/forecast-series/forecast-series.selectors';
import {
  BinLimitTypesAbcXyzWidget,
  DefaultSelectionTypesAbcXyzWidget,
  DefaultSelectionTypesPlanningAccuracyWidget,
  DefaultSelectionValuesAbcXyzWidget,
} from 'insights/types/widget-editor.types';
import { ResolutionPickerComponent } from '../../resolution-picker.component';

/**
 * General notes:
 * This component is ONLY for internal use and a super quick and sometimes dirty solution.
 * It is not meant to be a good example for a clean and well-structured component.
 * In case it should be refactored to a component which then can be accessed from the outside, please look at least at
 * - i18n
 * - error handling
 * - loading states
 * - after save, the page should not be reloaded completely
 * - better typings (and input validation!) for request body to backend
 * - feature flags (both on frontend and backend)
 * - backend side authorization
 */
@Component({
  selector: 'pm-edit-widget-view-dialog',
  templateUrl: './edit-widget-view-dialog.component.html',
  imports: [
    PmDialogComponent,
    DragDropModule,
    MatDividerModule,
    ResolutionPickerComponent,
    TargetUnitPickerComponent,
    TranslateModule,
    PmTooltipDirective,
    FormsModule,
    FormFieldComponent,
    FormInputNumberComponent,
    FormInputSelectComponent,
    FormInputTextComponent,
    PmButtonComponent,
    PmIconComponent,
  ],
})
export class EditWidgetViewDialogComponent {
  WidgetType = WidgetType;
  private readonly store = inject(Store);
  protected data = inject(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(
    MatDialogRef<EditWidgetViewDialogComponent>,
  );

  protected readonly trashIcon = LucideTrash;
  protected readonly dragIcon = LucideGripVertical;
  protected readonly plusIcon = LucidePlus;

  private readonly configurationHttpClient = inject(ConfigurationHttpClient);
  protected configuration = this.store.selectSignal(
    ConfigurationsSelectors.selectConfigurationByUrlParam,
  );
  protected availableFilters = this.store.selectSignal(
    selectAvailableFiltersAsRecord,
  );
  protected availableFiltersOptions = computed(() =>
    this.availableFilters().map((filter) => ({
      value: filter.column,
      label: filter.column,
    })),
  );

  protected availableFiltersOptionsForPortfolioTotals = computed(() => [
    ...this.availableFiltersOptions(),
    { value: this.TOTALS, label: 'Totals' },
  ]);

  protected yearlyComparisonFilterOptions = computed(() => [
    ...this.availableFiltersOptions(),
    { value: this.TOTALS, label: this.TOTALS },
  ]);

  protected possibleYears = this.store.selectSignal(
    selectForecastSeriesPossibleYears,
  );
  protected possibleYearsOptions = computed(() =>
    this.possibleYears().map((year) => ({
      value: year,
      label: year.toString(),
    })),
  );

  protected absoluteErrorMeasureOptions = absoluteErrorMeasureOptions.map(
    (option) => ({ value: option, label: option }),
  );
  protected relativeErrorMeasureOptions = relativeErrorMeasureOptions.map(
    (option) => ({ value: option, label: option.toUpperCase() }),
  );

  protected startForecastConfigGroupColumnOptions: Signal<
    NgxVirtualSelectFieldOptionModel<string>[]
  > = computed(
    () =>
      this.configuration()?.startForecastConfig.group_columns?.map((group) => ({
        value: group,
        label: group,
      })) ?? [],
  );

  protected TOTALS = TOTALS_NAME;

  widgets: Widget[];

  isDragging = false;

  supportedWidgets = [
    WidgetType.SINGLE_FORECASTS,
    WidgetType.PORTFOLIO_TOTALS,
    WidgetType.YEAR_TO_DATE_TREND,
    WidgetType.PORTFOLIO_TOTALS_MULTI_YEAR,
    WidgetType.YEARLY_COMPARISON,
    WidgetType.PLANNING_ACCURACY,
    WidgetType.ABC_XYZ_ANALYSIS,
    WidgetType.PLACEHOLDER,
  ];

  supportedWidgetOptions = this.supportedWidgets.map((widget) => ({
    value: widget,
    label: widget,
  }));

  protected numOfWidgets = signal(4);

  constructor() {
    this.widgets = [];
    const currentConfiguration = this.data.configuration;
    if (!currentConfiguration?.widgetView) {
      return;
    }
    const numberOfWidgets = currentConfiguration.widgetView.length;
    this.numOfWidgets.set(numberOfWidgets);
    this.widgets = Array.from(currentConfiguration.widgetView);
    // if there are not 4 widgets configured yet, push placeholder widget
    for (let i = this.widgets.length; i < numberOfWidgets; i += 1) {
      this.widgets.push({ type: WidgetType.PLACEHOLDER });
    }
    effect(() => {
      const updatedNumberOfWidgets = this.numOfWidgets();
      for (let i = this.widgets.length; i < updatedNumberOfWidgets; i += 1) {
        this.widgets.push({ type: WidgetType.PLACEHOLDER });
      }
    });
  }

  changedWidgetType(widgetType: WidgetType | WidgetType[], index: number) {
    if (Array.isArray(widgetType)) {
      return;
    }
    if (widgetType === WidgetType.ABC_XYZ_ANALYSIS) {
      this.setDefaultWidgetConfigurationForAbcXyzWidget(index);
    } else {
      this.widgets[index] = {
        name: this.widgets[index].name,
        type: widgetType,
      };
    }
  }

  setDefaultWidgetConfigurationForAbcXyzWidget(index: number) {
    const relativeVolumeInA = 0.8;
    const relativeVolumeInB = 0.95;
    const minimumForecastAccuracyInX = 0.7;
    const minimumForecastAccuracyInY = 0.3;

    this.widgets[index] = {
      name: this.widgets[index].name,
      type: WidgetType.ABC_XYZ_ANALYSIS,
      planningPeriod: 1,
      errorMeasure: Measure.Wape,
      volumeBinLimits: [relativeVolumeInA, relativeVolumeInB],
      forecastAccuracyBinLimits: [
        minimumForecastAccuracyInX,
        minimumForecastAccuracyInY,
      ],
      groups: [],
      timePeriodBackwardsInMonths: 0,
    };
  }

  changedWidgetName(event: Event, index: number) {
    const target = event.target as HTMLInputElement;
    this.widgets[index] = {
      ...this.widgets[index],
      name: target.value,
    };
  }

  changeResolution(resolution: Resolution, index: number) {
    const widget = this.widgets[index];

    // changing resolution is only possible for the following widgets that contain resolution in their type:
    // - WidgetType.PLANNING_ACCURACY
    // - WidgetType.YEARLY_COMPARISON
    // - WidgetType.SINGLE_FORECASTS
    const isResolutionChangeable =
      widget.type === WidgetType.PLANNING_ACCURACY ||
      widget.type === WidgetType.YEARLY_COMPARISON ||
      widget.type === WidgetType.SINGLE_FORECASTS;

    if (!widget || !isResolutionChangeable) {
      return;
    }

    this.widgets[index] = {
      ...widget,
      resolution,
    };
  }

  changeSingleForecastsFilter(
    filter: FilterValue | (FilterValue | undefined)[] | undefined,
    index: number,
    filterType: 'filterColumn' | 'filterOptions',
  ) {
    if (filterType === 'filterColumn') {
      if (typeof filter !== 'string') {
        return;
      }
      this.widgets[index] = {
        ...this.widgets[index],
        type: WidgetType.SINGLE_FORECASTS,
        filterColumn: filter,
        filterOptions: [],
      };
    } else {
      if (!Array.isArray(filter)) {
        return;
      }
      this.widgets[index] = {
        ...this.widgets[index],
        type: WidgetType.SINGLE_FORECASTS,
        filterOptions: filter.filter((f) => f !== undefined),
      };
    }
  }

  changeYearToDateFilter(
    filter: FilterValue | (FilterValue | undefined)[] | undefined,
    index: number,
    filterType: 'filterColumn' | 'filterOptions',
  ) {
    if (filterType === 'filterColumn') {
      if (typeof filter !== 'string') {
        return;
      }
      this.widgets[index] = {
        ...this.widgets[index],
        type: WidgetType.YEAR_TO_DATE_TREND,
        filterColumn: filter,
        filterOptions: [],
      };
    } else {
      if (!Array.isArray(filter)) {
        return;
      }
      this.widgets[index] = {
        ...this.widgets[index],
        type: WidgetType.YEAR_TO_DATE_TREND,
        filterOptions: filter.filter((f) => f !== undefined),
      };
    }
  }

  changePortfolioTotalsSettings(
    event: string | (string | undefined)[] | undefined,
    index: number,
    type: 'filter' | 'year',
  ) {
    if (!event) {
      return;
    }
    let valueInternal: string;
    if (Array.isArray(event)) {
      if (event[0] === undefined) {
        return;
      }
      [valueInternal] = event;
    } else {
      valueInternal = event;
    }
    if (!this.widgets[index]) {
      return;
    }

    this.widgets[index] = {
      ...this.widgets[index],
      type: WidgetType.PORTFOLIO_TOTALS,
      [type]: valueInternal,
    };
  }
  changeYearlyComparisonConfiguration(
    filter: FilterValue | (FilterValue | undefined)[] | undefined,
    index: number,
    filterType: 'filterColumn' | 'filterOptions',
  ) {
    if (filterType === 'filterColumn') {
      if (typeof filter !== 'string') {
        return;
      }
      this.widgets[index] = {
        ...this.widgets[index],
        type: WidgetType.YEARLY_COMPARISON,
        filterColumn: filter,
        filterOptions: '',
      };
    } else {
      if (Array.isArray(filter)) {
        return;
      }
      this.widgets[index] = {
        ...this.widgets[index],
        type: WidgetType.YEARLY_COMPARISON,
        filterOptions: filter,
      };
    }
  }

  changePortfolioTotalsMultiYearSettings(
    event: string | number | (string | number | undefined)[] | undefined,
    index: number,
    type: 'filter' | 'years',
  ) {
    if (!this.widgets[index]) {
      return;
    }
    if (!event) {
      return;
    }

    let valueInternal: string | number | (string | number)[];
    if (type === 'years') {
      if (Array.isArray(event)) {
        valueInternal = event.filter((e) => e !== undefined) as number[];
      } else {
        valueInternal = [event as number];
      }
    } else if (Array.isArray(event)) {
      if (event[0] === undefined) {
        return;
      }
      [valueInternal] = event;
    } else {
      valueInternal = event;
    }

    this.widgets[index] = {
      ...this.widgets[index],
      type: WidgetType.PORTFOLIO_TOTALS_MULTI_YEAR,
      [type]: valueInternal,
    };
  }

  changeTargetUnit(targetUnit: string, index: number) {
    if (!this.widgets[index] || !('targetUnit' in this.widgets[index])) {
      return;
    }
    this.widgets[index] = {
      ...this.widgets[index],
      targetUnit,
    };
  }

  drop(event: CdkDragDrop<Widget[]>) {
    moveItemInArray(this.widgets, event.previousIndex, event.currentIndex);
  }

  public readonly buttonConfig = {
    submitButton: { handleClick: () => this.save() },
    cancelButton: { handleClick: () => this.cancel() },
  };

  private cancel(): void {
    this.dialogRef.close(false);
  }
  private save(): void {
    const currentConfiguration = this.data.configuration;
    if (!currentConfiguration?.id) {
      console.error('No configuration id found, could not update widget view');
      return;
    }
    this.configurationHttpClient
      .updateConfigurationWidgetView(currentConfiguration.id, this.widgets)
      .subscribe(() => {
        this.dialogRef.close(true);
      });
  }

  getFilterOptionsOfFilterColumnTrendAnalysis(
    index: number,
  ): NgxVirtualSelectFieldOptionModel<FilterValue>[] {
    const widget = this.widgets[index];
    if (widget?.type !== WidgetType.YEAR_TO_DATE_TREND) {
      return [];
    }
    return (
      this.availableFilters().find(
        (filter) => filter.column === widget.filterColumn,
      )?.values ?? []
    ).map((value) => ({
      value,
      label: String(value),
    }));
  }

  getFilterOptionsOfFilterColumnYearlyComparison(
    index: number,
  ): NgxVirtualSelectFieldOptionModel<FilterValue>[] {
    const widget = this.widgets[index];
    if (widget?.type !== WidgetType.YEARLY_COMPARISON) {
      return [];
    }
    return (
      this.availableFilters().find(
        (filter) => filter.column === widget.filterColumn,
      )?.values ?? []
    ).map((value) => ({
      value,
      label: String(value),
    }));
  }

  getFilterOptionsOfFilterColumnSingleForecast(
    index: number,
  ): NgxVirtualSelectFieldOptionModel<FilterValue>[] {
    const widget = this.widgets[index];
    if (widget?.type !== WidgetType.SINGLE_FORECASTS) {
      return [];
    }
    return (
      this.availableFilters().find(
        (filter) => filter.column === widget.filterColumn,
      )?.values ?? []
    ).map((value) => ({
      value,
      label: String(value),
    }));
  }
  addWidget() {
    this.numOfWidgets.update((num) => num + 1);
  }

  removeWidget(idx: number) {
    this.widgets.splice(idx, 1);
    this.numOfWidgets.update((num) => num - 1);
  }

  getFilterOptionsOfFilterGroupPlanningAccuracy(
    index: number,
  ): NgxVirtualSelectFieldOptionModel<FilterValue>[] {
    const widget = this.widgets[index];
    if (widget?.type !== WidgetType.PLANNING_ACCURACY) {
      return [];
    }
    return (
      this.availableFilters().find(
        (filter) => filter.column === widget.selectedFilterGroup,
      )?.values ?? []
    ).map((value) => ({ value, label: String(value) }));
  }

  changeDefaultFilterSelectionOfPlanningAccuracyWidget(
    filterValue: FilterValue | (FilterValue | undefined)[] | undefined,
    index: number,
    selectionType: DefaultSelectionTypesPlanningAccuracyWidget,
  ) {
    if (!this.widgets[index]) {
      return;
    }
    if (!filterValue) {
      return;
    }
    let filterValueInternal: FilterValue;
    if (Array.isArray(filterValue)) {
      if (filterValue[0] !== undefined) {
        [filterValueInternal] = filterValue;
      } else {
        filterValueInternal = '';
      }
    } else {
      filterValueInternal = filterValue;
    }
    this.widgets[index] = {
      ...this.widgets[index],
      [selectionType]: filterValueInternal,
    };
  }

  changeDefaultFilterSelectionOfAbcXyzWidget(
    selectionValue:
      | DefaultSelectionValuesAbcXyzWidget
      | (string | undefined)[]
      | undefined,
    index: number,
    selectionType: DefaultSelectionTypesAbcXyzWidget,
  ) {
    if (!this.widgets[index]) {
      return;
    }

    this.widgets[index] = {
      ...this.widgets[index],
      [selectionType]: selectionValue,
    };
  }

  setPlanningPeriod(value: number, index: number): void {
    const widget = this.widgets[index];
    if (widget?.type !== WidgetType.PLANNING_ACCURACY) {
      return;
    }
    widget.planningPeriod = value;
  }

  get additionalTimeSeries(): string[] {
    return this.data.configuration?.additionalTimeSeriesNames ?? [];
  }

  protected additionalTimeSeriesOptions = computed(() =>
    this.additionalTimeSeries.map((name) => ({
      value: name,
      label: name,
    })),
  );

  setBinLimitsOfAbcXyzAnalysisWidget(
    value: string,
    index: number,
    type: BinLimitTypesAbcXyzWidget,
  ): void {
    const regex = /^\s*-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?\s*$/;
    const isCommaSeperatedNumbers = regex.test(value);

    if (isCommaSeperatedNumbers) {
      this.widgets[index] = {
        ...this.widgets[index],
        [type]: value
          .replace(/\s+/g, '')
          .split(',')
          .map((v) => Number.parseFloat(v)),
      };
    }
  }

  setTimePeriodBackwardsInMonthsForAbcXyzAnalysisWidget(
    value: number,
    index: number,
  ): void {
    const widget = this.widgets[index];
    if (widget?.type !== WidgetType.ABC_XYZ_ANALYSIS) {
      return;
    }
    widget.timePeriodBackwardsInMonths = value;
  }
}
